import React, { useEffect, useState } from "react";
import {
  useAppKitAccount,
  useAppKit,
} from "@reown/appkit/react";
import useContract from "../hooks/useContract";
import { toast } from "react-hot-toast";
import { Contract, formatUnits, JsonRpcProvider } from "ethers";

const RPC_URL = "https://bsc-dataseed.binance.org";

const Dashboard = () => {
  const { address, isConnected } = useAppKitAccount();
  const { open } = useAppKit();

  const {
    getUserDashboard,
    getReferralStats,
    getReferralEarnings, // 🔥 ADDED: get user's referral earnings
    totalClaimable,
    getGlobalStats,
    getCoreGlobalStats,
    getGlobalRewardStats,
    getSystemHealth,
  } = useContract();

  const [data, setData] = useState({
    totalPackages: 0,
    activePackages: 0,
    totalSpent: 0,
    totalClaimed: 0,
    claimable: 0,
  });

  const [refStats, setRefStats] = useState({
    direct: 0,
    team: 0,
    referralEarnings: 0, // 🔥 RENAMED & FIXED: User's actual earnings
  });

  const [globalStats, setGlobalStats] = useState({
    totalPackagesBought: 0,
    totalKINDFlow: 0,
    totalRewardsDistributed: 0,
    totalBurned: 0,
    totalRefundsPaid: 0,
    totalClaimableGlobal: 0,
    totalRewardsDistributedCore: 0,
    activePackagesTotal: 0,
    totalClaimedStats: 0,
    totalClaimableGlobalStats: 0,
    rewardReserve: 0,
    refundReserve: 0,
    contractBalance: 0,
  });

  const [loading, setLoading] = useState(true);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [kindPrice, setKindPrice] = useState(0);

  const provider = new JsonRpcProvider(RPC_URL);

  const KIND_ADDRESS = "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";
  const KIND_BNB_PAIR = "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

  // 🔥 REFRESH FUNCTIONS WITH REFERRAL EARNINGS
 // 🔥 FIXED: refreshUserStats - Now uses BOTH getReferralStats & getReferralEarnings
const refreshUserStats = async () => {
  if (!isConnected || !address) return;

  setLoading(true);

  try {
    const [
      dashboardRes,
      claimRes,
      referralStatsRes,
      referralEarningsRes,
    ] = await Promise.all([
      getUserDashboard(),
      totalClaimable(),
      getReferralStats(),
      getReferralEarnings(),
    ]);

    // ✅ CLAIM FORMAT
    const formattedClaim = Number(
      typeof claimRes === "string" && claimRes.includes(".")
        ? claimRes
        : formatUnits(claimRes, 18)
    );

    // ✅ DASHBOARD DATA
    setData({
      totalPackages: dashboardRes.totalPackages,
      activePackages: dashboardRes.activePackages,
      totalSpent: Number(
        typeof dashboardRes.totalSpent === "string" &&
        dashboardRes.totalSpent.includes(".")
          ? dashboardRes.totalSpent
          : formatUnits(dashboardRes.totalSpent, 18)
      ).toFixed(2),
      totalClaimed: Number(
        typeof dashboardRes.totalClaimed === "string" &&
        dashboardRes.totalClaimed.includes(".")
          ? dashboardRes.totalClaimed
          : formatUnits(dashboardRes.totalClaimed, 18)
      ).toFixed(2),
      claimable: Number(formattedClaim.toFixed(2)),
    });

    // 🔥 REFERRAL STATS
    let direct = 0;
    let team = 0;

    if (
      referralStatsRes &&
      typeof referralStatsRes === "object" &&
      !Array.isArray(referralStatsRes)
    ) {
      direct = Number(referralStatsRes.direct || 0);
      team = Number(referralStatsRes.team || 0);
    } else if (Array.isArray(referralStatsRes) && referralStatsRes.length === 2) {
      direct = Number(referralStatsRes[0] || 0);
      team = Number(referralStatsRes[1] || 0);
    } else if (Array.isArray(referralStatsRes) && Array.isArray(referralStatsRes[1])) {
      const levels = referralStatsRes[1];
      direct = Number(levels[0] || 0);
      team = levels.reduce((acc, val) => acc + Number(val || 0), 0);
    }

    // 🔥 REFERRAL EARNINGS
    let earningsRaw = Array.isArray(referralEarningsRes)
      ? referralEarningsRes[0]
      : referralEarningsRes;

    const referralEarningsFormatted = Number(
      typeof earningsRaw === "string" && earningsRaw.includes(".")
        ? earningsRaw
        : formatUnits(earningsRaw || 0, 18)
    ).toFixed(2);

    // ✅ FINAL
    setRefStats({
      direct: direct.toFixed(0),
      team: team.toFixed(0),
      referralEarnings: referralEarningsFormatted,
    });

  } catch (err) {
    console.error("refreshUserStats error:", err);
  } finally {
    setLoading(false);
  }
};


  const refreshGlobalStats = async () => {
  if (!isConnected) return;

  setGlobalLoading(true);

  try {
    const [
      globalRes,
      coreRes,
      rewardRes,
      healthRes,
    ] = await Promise.all([
      getGlobalStats(),
      getCoreGlobalStats(),
      getGlobalRewardStats(),
      getSystemHealth(),
    ]);

    // 🔥 SAFE FORMAT HELPER
    const safeFormat = (val, decimals = 18) => {
      try {
        if (Array.isArray(val)) return 0; // prevent crash
        if (typeof val === "string" && val.includes(".")) return Number(val);
        return Number(formatUnits(val || 0, decimals));
      } catch {
        return 0;
      }
    };

    setGlobalStats({
      totalPackagesBought: safeFormat(globalRes?.[0], 0),
      totalKINDFlow: safeFormat(globalRes?.[1], 18),
      totalRewardsDistributed: safeFormat(globalRes?.[2], 18),

      totalBurned: safeFormat(coreRes?.[0], 18),
      totalRefundsPaid: safeFormat(coreRes?.[1], 18),
      totalClaimableGlobal: safeFormat(coreRes?.[2], 18),
      totalRewardsDistributedCore: safeFormat(coreRes?.[3], 18),
      activePackagesTotal: safeFormat(coreRes?.[4], 0),

      totalClaimedStats: safeFormat(rewardRes?.[0], 18),
      totalClaimableGlobalStats: safeFormat(rewardRes?.[1], 18),
      rewardReserve: safeFormat(rewardRes?.[2], 18),
      refundReserve: safeFormat(rewardRes?.[3], 18),
      contractBalance: safeFormat(rewardRes?.[4], 18),
    });

  } catch (err) {
    console.error("refreshGlobalStats error:", err);
  } finally {
    setGlobalLoading(false);
  }
};

  const fetchKindPrice = async () => {
    try {
      const pair = new Contract(
        KIND_BNB_PAIR,
        [
          "function getReserves() view returns (uint112,uint112,uint32)",
          "function token0() view returns (address)",
        ],
        provider
      );

      const reserves = await pair.getReserves();
      const token0 = await pair.token0();

      let kindReserve, bnbReserve;
      if (token0.toLowerCase() === KIND_ADDRESS.toLowerCase()) {
        kindReserve = reserves[0];
        bnbReserve = reserves[1];
      } else {
        kindReserve = reserves[1];
        bnbReserve = reserves[0];
      }

      const kindReserveNum = parseFloat(formatUnits(kindReserve, 18));
      const bnbReserveNum = parseFloat(formatUnits(bnbReserve, 18));
      const kindPerBNB = bnbReserveNum / kindReserveNum;

      const priceFeed = new Contract(
        "0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE",
        [
          "function latestRoundData() view returns (uint80,int256,uint256,uint256,uint80)",
          "function decimals() view returns (uint8)",
        ],
        provider
      );

      const roundData = await priceFeed.latestRoundData();
      const decimals = await priceFeed.decimals();
      const bnbUsd = parseFloat(formatUnits(roundData[1], decimals));

      setKindPrice(kindPerBNB * bnbUsd);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchKindPrice();
    const i = setInterval(fetchKindPrice, 15000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (isConnected) {
      refreshGlobalStats();
      const interval = setInterval(refreshGlobalStats, 30000);
      return () => clearInterval(interval);
    }
  }, [isConnected]);

  useEffect(() => {
    if (!address || !isConnected) return;
    refreshUserStats();
  }, [address, isConnected]);

  const getUSD = (amount) => {
    if (!kindPrice || isNaN(kindPrice)) return "0.00";
    return (Number(amount) * Number(kindPrice)).toFixed(2);
  };

  const referralLink = `${window.location.origin}/packages?ref=${address}`;
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  if (!isConnected) {
    return (
<div className="flex items-center justify-center min-h-[100dvh] bg-black px-4">
  <div
    className="w-full max-w-md bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f]
    border border-blue-500 rounded-2xl p-6 md:p-8 text-center
    flex flex-col justify-center shadow-xl"
  >
    {/* Spinner */}
    <div className="w-12 h-12 mx-auto mb-3 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

    {/* Title */}
    <h2 className="text-xl md:text-2xl font-bold text-blue-400 mb-2">
      Connect Wallet
    </h2>

    {/* Subtitle */}
    <p className="text-gray-400 mb-5 text-sm">
      Please connect wallet to view your dashboard
    </p>

    {/* Button */}
    <button
      onClick={() => open()}
      className="w-full bg-gradient-to-r from-blue-500 to-orange-500 py-3 rounded-xl font-semibold hover:opacity-90 transition"
    >
      Connect Wallet
    </button>
  </div>
</div>
    );
  }

  return (
    <div className="p-4 md:p-6 text-white min-h-screen">
      {/* 🔥 HEADER WITH REFRESH BUTTONS */}
      <div className="bg-gradient-to-r from-[#0f1b2d] via-[#0a0f1c] to-[#1a0f1f] p-6 rounded-2xl mb-6">
        <div className="flex justify-between items-start mb-3">
          <h1 className="text-2xl md:text-4xl font-bold text-blue-400">Welcome back!</h1>
          <div className="flex gap-2">
            <button
              onClick={refreshUserStats}
              disabled={loading}
              className="bg-blue-600/50 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 disabled:opacity-50"
              title="Refresh User Stats"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "🔄"
              )}
              <span className="hidden sm:inline text-white">User</span>
            </button>
            <button
              onClick={refreshGlobalStats}
              disabled={globalLoading}
              className="bg-green-600/50 hover:bg-green-600 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 disabled:opacity-50"
              title="Refresh Global Stats"
            >
              {globalLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "🔄"
              )}
              <span className="hidden sm:inline text-white">Global</span>
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-3 text-green-400 text-sm">
          <div className="bg-green-900 p-2 rounded-lg">🔒</div>
          <span>{address.slice(0, 6)}...{address.slice(-4)}</span>
        </div>
        <div className="mt-3 text-yellow-400 text-sm">
          KIND: ${kindPrice > 0 ? kindPrice.toFixed(6) : "Loading..."}
        </div>
      </div>

      {/* 🔥 USER DASHBOARD */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <span>📊</span> Your Dashboard
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card title="TOTAL PACKAGES" value={data.totalPackages} color="blue" loading={loading} />
          <Card 
            title="CLAIMABLE" 
            value={data.claimable} 
            color="green" 
            loading={loading} 
            getUSD={getUSD}
            showKind={true}
          />
          <Card title="TOTAL CLAIMED" value={data.totalClaimed} color="purple" loading={loading} getUSD={getUSD} />
          <Card title="TOTAL SPENT" value={data.totalSpent} color="yellow" loading={loading} getUSD={getUSD} />
          <Card title="ACTIVE PACKAGES" value={data.activePackages} color="cyan" loading={loading} />
          <Card title="REFERRAL SYSTEM" value="Enabled" color="indigo" />
        </div>
      </div>

      {/* 🔥 GLOBAL STATS SECTION */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <span>🌍</span> Global Network Stats
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card title="TOTAL PACKAGES" value={globalStats.totalPackagesBought.toLocaleString()} color="blue" loading={globalLoading} />
          <Card title="ACTIVE PACKAGES" value={globalStats.activePackagesTotal.toLocaleString()} color="cyan" loading={globalLoading} />
          <Card title="TOTAL VOLUME" value={globalStats.totalKINDFlow.toFixed(0)} color="yellow" loading={globalLoading} getUSD={getUSD} />
          <Card title="REWARDS DISTRIBUTED" value={globalStats.totalRewardsDistributed.toFixed(0)} color="purple" loading={globalLoading} getUSD={getUSD} />
          <Card title="TOKENS BURNED" value={globalStats.totalBurned.toFixed(0)} color="red" loading={globalLoading} getUSD={getUSD} />
          <Card title="REFUNDS PAID" value={globalStats.totalRefundsPaid.toFixed(0)} color="orange" loading={globalLoading} getUSD={getUSD} />
        </div>
      </div>

      {/* 🔥 CONTRACT HEALTH */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <span>⚕️</span> Contract Health
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card title="CONTRACT BALANCE" value={globalStats.contractBalance.toFixed(2)} color="green" loading={globalLoading} getUSD={getUSD} />
          <Card title="REWARD RESERVE" value={globalStats.rewardReserve.toFixed(2)} color="blue" loading={globalLoading} getUSD={getUSD} />
          <Card title="REFUND RESERVE" value={globalStats.refundReserve.toFixed(2)} color="yellow" loading={globalLoading} getUSD={getUSD} />
          <Card title="GLOBAL CLAIMABLE" value={globalStats.totalClaimableGlobal.toFixed(0)} color="purple" loading={globalLoading} getUSD={getUSD} />
        </div>
      </div>

      {/* 🔥 REFERRAL SECTION - FIXED WITH EARNINGS BOX */}
      <div className="bg-gradient-to-r from-[#1c1c3a] to-[#0f172a] p-6 rounded-2xl border border-purple-600">
        <h2 className="text-xl mb-6 text-purple-400 flex items-center gap-2">
          <span>👥</span> Referral Network
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-black/40 border border-pink-500 p-4 rounded-xl">
            <p className="text-gray-400 text-sm">Direct Referrals</p>
            <h2 className="text-2xl font-bold text-pink-400 mt-1">{refStats.direct}</h2>
          </div>
          <div className="bg-black/40 border border-indigo-500 p-4 rounded-xl">
            <p className="text-gray-400 text-sm">Total Team</p>
            <h2 className="text-2xl font-bold text-indigo-400 mt-1">{refStats.team}</h2>
          </div>
          {/* 🔥 FIXED: User's Referral Earnings Box */}
          <div className="bg-black/40 border border-green-500 p-4 rounded-xl">
            <p className="text-gray-400 text-sm">Your Referral Earnings</p>
            <h2 className="text-2xl font-bold text-green-400 mt-1">
              {refStats.referralEarnings} KIND
              <div className="text-xs text-gray-400 mt-1">(${getUSD(refStats.referralEarnings)})</div>
            </h2>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-gray-400 mb-1">Referral Link</p>
          <div className="flex gap-2">
            <input value={referralLink} readOnly className="w-full bg-black border border-gray-700 p-3 rounded-lg text-sm" />
            <button onClick={() => copy(referralLink)} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors">
              Copy
            </button>
          </div>
        </div>

        <div>
          <p className="text-gray-400 mb-1">Wallet Address</p>
          <div className="flex gap-2">
            <input value={address} readOnly className="w-full bg-black border border-gray-700 p-3 rounded-lg text-sm" />
            <button onClick={() => copy(address)} className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors">
              Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 🔥 UPDATED CARD COMPONENT
const Card = ({ title, value, color, loading, getUSD, showKind = false }) => {
  const colors = {
    blue: "from-blue-500/20 to-blue-900/20 border-blue-500 text-blue-400",
    green: "from-green-500/20 to-green-900/20 border-green-500 text-green-400",
    purple: "from-purple-500/20 to-purple-900/20 border-purple-500 text-purple-400",
    yellow: "from-yellow-500/20 to-yellow-900/20 border-yellow-500 text-yellow-400",
    cyan: "from-cyan-500/20 to-cyan-900/20 border-cyan-500 text-cyan-400",
    indigo: "from-indigo-500/20 to-indigo-900/20 border-indigo-500 text-indigo-400",
    red: "from-red-500/20 to-red-900/20 border-red-500 text-red-400",
    orange: "from-orange-500/20 to-orange-900/20 border-orange-500 text-orange-400",
  };

  const isPackageCount = title.includes("PACKAGES") && !showKind;
  const isTokenAmount = title.includes("CLAIMABLE") || showKind || !isPackageCount;

  return (
    <div className={`p-4 md:p-6 rounded-2xl bg-gradient-to-br ${colors[color] || colors.blue} border transition-all hover:scale-[1.02] hover:shadow-2xl`}>
      <p className="text-gray-400 text-xs md:text-sm mb-2 line-clamp-2">{title}</p>

      {loading ? (
        <div className="flex justify-center">
          <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div>
          <h2 className="text-lg md:text-2xl font-bold text-white leading-tight">
            {typeof value === "number" ? value : value}
            {isPackageCount ? (
              <span className="text-xs ml-1">pkgs</span>
            ) : (
              <>
                {" "}KIND
                {getUSD && isTokenAmount && (
                  <div className="text-xs text-gray-400 mt-1 font-normal">
                    (${getUSD(value)})
                  </div>
                )}
              </>
            )}
          </h2>
        </div>
      )}
    </div>
  );
};

export default Dashboard;